import { Component, Input, OnInit } from '@angular/core';
import {
  CostsReportsDataModel,
  CostsReportsFieldDataModel,
  CostsReportsTerminalDataModel,
  CostsReportsWaterSystemDataModel,
  CostsReportsGroupDataModel,
  CostReportsItemDataModel,
  CostsReportsCostDataModel,
} from '../../reports.model';
import { TranslationsLibService } from '@nutricontrol/app360-shared';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-reports-costs-table',
  templateUrl: './reports-costs-table.component.html',
  styleUrls: ['./reports-costs-table.component.scss'],
})
export class ReportsCostsTableComponent implements OnInit {
  @Input() costsReportsData: CostsReportsDataModel;

  stickyDisplayedColumns: string[] = [];
  displayedColumns: string[] = [];
  stickyTopHeaderColumns: string[] = [];
  topHeaderColumns: string[] = [];

  tables: TableRow[][] = [];

  valvesForm: FormControl[] = [];
  valvesOptions: ValveSelectOption[][] = [];

  showDetails = false;

  terminalsWithoutIrrigation = [];
  constructor(public translationsLib: TranslationsLibService) {}

  ngOnInit(): void {
    console.log('#COSTS REPORTS DATA#', this.costsReportsData);
    if (this.costsReportsData && this.costsReportsData.items.length > 0) {
      this.buildDisplayedColumns();
      this.buildValveFilter();
      this.buildTables();
      this.setTerminalsWithoutIrrigation();
    }
  }

  setTerminalsWithoutIrrigation(): void {
    this.costsReportsData.items.forEach((reportItem) => {
      reportItem.fields.forEach((fieldData) => {
        fieldData.terminals.forEach((terminalData) => {
          if (terminalData.water_systems.length === 0) {
            this.terminalsWithoutIrrigation.push({
              name: terminalData.terminal_name,
              vid: terminalData.terminal_vid,
            });
          }
        });
      });
    });
  }

  onValvesSelectionChange() {
    this.buildTables();
  }

  buildValveFilter(): void {
    this.valvesOptions = [];
    this.valvesForm = [];

    this.costsReportsData.items.forEach((reportItem, tableIndex) => {
      this.valvesOptions[tableIndex] = [];
      reportItem.fields.forEach((fieldData) => {
        fieldData.terminals.forEach((terminalData) => {
          terminalData.water_systems.forEach((waterSystemData) => {
            waterSystemData.groups.forEach((groupData) => {
              groupData.valves.forEach((valveData) => {
                this.valvesOptions[tableIndex].push({
                  value: this.valveIdentifier(
                    fieldData.field_name,
                    terminalData.terminal_name,
                    'C' + (waterSystemData.number + 1),
                    groupData.name,
                    valveData.name
                  ),
                  label: this.valveIdentifier(
                    fieldData.field_name,
                    terminalData.terminal_name,
                    'C' + (waterSystemData.number + 1),
                    groupData.name,
                    valveData.name
                  ),
                });
              });
            });
          });
        });
      });
      this.valvesForm[tableIndex] = new FormControl(
        this.valvesOptions[tableIndex].map((valve) => valve.value)
      );
    });
  }

  buildDisplayedColumns(): void {
    this.stickyTopHeaderColumns = ['empty', 'totals'];
    this.stickyDisplayedColumns = [
      'field',
      'terminal',
      'water_system',
      'group',
      'valve',
      'theoretical_total',
      'total',
      'total_consumption',
      'total_comparative_deviation',
    ];

    this.topHeaderColumns = [];
    this.displayedColumns = [];
    this.costsReportsData.time_ranges.forEach((_, index) => {
      this.topHeaderColumns.push(`range_${index}`);
      this.displayedColumns.push(`consumption_${index}`);
      this.displayedColumns.push(`water_price_${index}`);
      this.displayedColumns.push(`water_cost_${index}`);
      if (index > 0) {
        this.displayedColumns.push(`comp_deviation_${index}`);
      }
    });
  }

  buildTables(): void {
    const tables = [];

    const costsReportsDataItems = this.costsReportsData.items;

    costsReportsDataItems.forEach((reportItem, tableIndex) => {
      const tableRows: TableRow[] = [];

      reportItem.fields.forEach((fieldData) => {
        let showFieldCell = true;
        fieldData.terminals.forEach((terminalData) => {
          let showTerminalCell = true;
          terminalData.water_systems.forEach((waterSystemData) => {
            let showWaterSystemCell = true;
            const visibleGroups = waterSystemData.groups.filter((groupData) => {
              const visibleValves = groupData.valves.filter((valveData) => {
                return !this.isExcludedValve(
                  tableIndex,
                  fieldData.field_name,
                  terminalData.terminal_name,
                  'C' + (waterSystemData.number + 1),
                  groupData.name,
                  valveData.name
                );
              });
              return visibleValves.length > 0;
            });
            waterSystemData.groups.forEach((groupData, groupIndex) => {
              let showGroupCell = true;
              const visibleValves = groupData.valves.filter((valveData) => {
                return !this.isExcludedValve(
                  tableIndex,
                  fieldData.field_name,
                  terminalData.terminal_name,
                  'C' + (waterSystemData.number + 1),
                  groupData.name,
                  valveData.name
                );
              });

              visibleValves.forEach((valveData, valveIndex) => {
                const isLastGroupValve =
                  valveIndex === visibleValves.length - 1;
                const isLastWaterSystemGroup =
                  groupIndex === visibleGroups.length - 1;
                tableRows.push({
                  field: fieldData.field_name,
                  terminal: terminalData.terminal_name,
                  terminal_vid: terminalData.terminal_vid,
                  water_system: 'C' + (waterSystemData.number + 1),
                  group: groupData.name,
                  valve: valveData.name,
                  costs: valveData.costs,
                  theoretical_total: valveData.totals.theoretical_total,
                  total: valveData.totals.total,
                  water_cost: valveData.totals.water_cost,
                  total_comparative_deviation:
                    valveData.totals.total_comparative_deviation,
                  field_rowspan: this.calculateFieldRowSpan(
                    fieldData,
                    tableIndex
                  ),
                  terminal_rowspan: this.calculateTerminalRowSpan(
                    terminalData,
                    fieldData,
                    tableIndex
                  ),
                  water_system_rowspan: this.calculateWaterSystemRowSpan(
                    waterSystemData,
                    fieldData,
                    terminalData,
                    tableIndex
                  ),
                  group_rowspan: this.calculateGroupRowSpan(
                    groupData,
                    waterSystemData,
                    fieldData,
                    terminalData,
                    tableIndex
                  ),
                  field_colspan: 1,
                  terminal_colspan: 1,
                  water_system_colspan: 1,
                  group_colspan: 1,
                  field_show_cell: showFieldCell,
                  terminal_show_cell: showTerminalCell,
                  water_system_show_cell: showWaterSystemCell,
                  group_show_cell: showGroupCell,
                  valve_show_cell: true,
                  is_last_group_valve: isLastGroupValve,
                  is_last_water_system_group: isLastWaterSystemGroup,
                  currency: {
                    symbol: reportItem.currency.symbol,
                    space_separator: reportItem.currency.space_separator,
                    symbol_position: reportItem.currency.symbol_position,
                  },
                  is_totals: false,
                });

                showFieldCell = false;
                showTerminalCell = false;
                showWaterSystemCell = false;
                showGroupCell = false;
              });
            });
            if (!showWaterSystemCell) {
              // Total cabezal
              const waterSystemTotalRow = this.calculateWaterSystemsTotals(
                waterSystemData,
                reportItem,
                tableIndex,
                fieldData,
                terminalData
              );
              tableRows.push(waterSystemTotalRow);
            }
          });

          // Total terminal
          if (terminalData.water_systems.length > 0 && !showTerminalCell) {
            const terminalTotalRow = this.calculateTerminalTotals(
              terminalData,
              reportItem,
              tableIndex,
              fieldData
            );
            tableRows.push(terminalTotalRow);
          }
        });

        if (!showFieldCell) {
          // Total finca
          const fieldTotalRow = this.calculateFieldTotals(
            fieldData,
            reportItem,
            tableIndex
          );

          tableRows.push(fieldTotalRow);
        }
      });

      // Gran total (de todas las fincas agrupadas por monedas)
      const grandTotalRow = this.calculateGrandTotals(reportItem, tableIndex);
      tableRows.push(grandTotalRow);

      tables.push(tableRows);
    });

    console.log('#882#', tables);
    this.tables = tables;
  }

  private createEmptyCosts(length: number): CostsReportsCostDataModel[] {
    return Array.from({ length }, (_, index): CostsReportsCostDataModel => {
      const emptyCost: CostsReportsCostDataModel = {
        consumption_m3: 0,
        water_price_m3: 0,
        water_cost: 0,
      };

      if (index > 0) {
        emptyCost.comparative_deviation = 0;
      }

      return emptyCost;
    });
  }

  private sumValveCosts(
    target: CostsReportsCostDataModel[],
    source: CostsReportsCostDataModel[]
  ): void {
    source.forEach((src, i) => {
      target[i].consumption_m3 += src.consumption_m3;
      target[i].water_price_m3 += src.water_price_m3;
      target[i].water_cost += src.water_cost;
      if (
        target[i].comparative_deviation !== null &&
        src.comparative_deviation !== null
      ) {
        target[i].comparative_deviation += src.comparative_deviation;
      }
    });
  }

  calculateWaterSystemsTotals(
    waterSystemData: CostsReportsWaterSystemDataModel,
    currencyData: CostReportsItemDataModel,
    tableIndex: number,
    fieldData: CostsReportsFieldDataModel,
    terminalData: CostsReportsTerminalDataModel
  ): TableRow {
    let theoretical_total = 0;
    let total = 0;
    let total_comparative_deviation = 0;
    let water_cost = 0;
    let numValves = 0;

    const costs = this.createEmptyCosts(
      this.costsReportsData.time_ranges.length
    );

    waterSystemData.groups.forEach((groupData) => {
      groupData.valves.forEach((valveData) => {
        if (
          this.isExcludedValve(
            tableIndex,
            fieldData.field_name,
            terminalData.terminal_name,
            'C' + (waterSystemData.number + 1),
            groupData.name,
            valveData.name
          )
        ) {
          return;
        }
        numValves++;
        this.sumValveCosts(costs, valveData.costs);
        theoretical_total += valveData.totals.theoretical_total;
        total += valveData.totals.total;
        total_comparative_deviation +=
          valveData.totals.total_comparative_deviation;
        water_cost += valveData.totals.water_cost;
      });
    });
    costs.forEach((cost) => {
      cost.water_price_m3 = cost.water_price_m3 / numValves;
    });

    return {
      group:
        this.translationsLib.get('irrigation_total') +
        `&nbsp;C${waterSystemData.number + 1}`,
      group_show_cell: true,
      valve_show_cell: false,
      group_colspan: 2,
      group_rowspan: 1,
      is_totals: true,
      is_total_water_system: true,
      is_last_group_valve: false,
      is_last_water_system_group: false,
      costs,
      theoretical_total,
      total,
      total_comparative_deviation,
      water_cost,
      currency: {
        symbol: currencyData.currency.symbol,
        space_separator: currencyData.currency.space_separator,
        symbol_position: currencyData.currency.symbol_position,
      },
    };
  }

  calculateTerminalTotals(
    terminalData: CostsReportsTerminalDataModel,
    currencyData: CostReportsItemDataModel,
    tableIndex: number,
    fieldData: CostsReportsFieldDataModel = null
  ): TableRow {
    let theoretical_total = 0;
    let total = 0;
    let total_comparative_deviation = 0;
    let water_cost = 0;
    let numValves = 0;

    const costs = this.createEmptyCosts(
      this.costsReportsData.time_ranges.length
    );

    terminalData.water_systems.forEach((waterSystem) => {
      waterSystem.groups.forEach((groupData) => {
        groupData.valves.forEach((valveData) => {
          if (
            this.isExcludedValve(
              tableIndex,
              fieldData.field_name,
              terminalData.terminal_name,
              'C' + (waterSystem.number + 1),
              groupData.name,
              valveData.name
            )
          ) {
            return;
          }
          numValves++;
          this.sumValveCosts(costs, valveData.costs);
          theoretical_total += valveData.totals.theoretical_total;
          total += valveData.totals.total;
          total_comparative_deviation +=
            valveData.totals.total_comparative_deviation;
          water_cost += valveData.totals.water_cost;
        });
      });
    });

    costs.forEach((cost) => {
      cost.water_price_m3 = cost.water_price_m3 / numValves;
    });

    return {
      group:
        this.translationsLib.get('irrigation_total') +
        `&nbsp;${terminalData.terminal_name}`,
      terminal_show_cell: false,
      group_show_cell: true,
      valve_show_cell: false,
      group_colspan: 3,
      group_rowspan: 1,
      is_totals: true,
      is_total_terminal: true,
      is_last_group_valve: false,
      is_last_water_system_group: false,
      costs,
      theoretical_total,
      total,
      total_comparative_deviation,
      water_cost,
      currency: {
        symbol: currencyData.currency.symbol,
        space_separator: currencyData.currency.space_separator,
        symbol_position: currencyData.currency.symbol_position,
      },
    };
  }

  calculateFieldTotals(
    fieldData: CostsReportsFieldDataModel,
    currencyData: CostReportsItemDataModel,
    tableIndex: number
  ): TableRow {
    let theoretical_total = 0;
    let total = 0;
    let total_comparative_deviation = 0;
    let water_cost = 0;
    let numValves = 0;

    const costs = this.createEmptyCosts(
      this.costsReportsData.time_ranges.length
    );

    fieldData.terminals.forEach((terminalData) => {
      terminalData.water_systems.forEach((waterSystem) => {
        waterSystem.groups.forEach((groupData) => {
          groupData.valves.forEach((valveData) => {
            if (
              this.isExcludedValve(
                tableIndex,
                fieldData.field_name,
                terminalData.terminal_name,
                'C' + (waterSystem.number + 1),
                groupData.name,
                valveData.name
              )
            ) {
              return;
            }
            numValves++;
            this.sumValveCosts(costs, valveData.costs);
            theoretical_total += valveData.totals.theoretical_total;
            total += valveData.totals.total;
            total_comparative_deviation +=
              valveData.totals.total_comparative_deviation;
            water_cost += valveData.totals.water_cost;
          });
        });
      });
    });
    costs.forEach((cost) => {
      cost.water_price_m3 = cost.water_price_m3 / numValves;
    });

    return {
      group:
        this.translationsLib.get('irrigation_total') +
        `&nbsp;${fieldData.field_name}`,
      group_show_cell: true,
      valve_show_cell: false,
      group_colspan: 4,
      group_rowspan: 1,
      is_totals: true,
      is_total_field: true,
      is_last_group_valve: false,
      is_last_water_system_group: false,
      costs,
      theoretical_total,
      total,
      total_comparative_deviation,
      water_cost,
      currency: {
        symbol: currencyData.currency.symbol,
        space_separator: currencyData.currency.space_separator,
        symbol_position: currencyData.currency.symbol_position,
      },
    };
  }

  calculateGrandTotals(
    currencyData: CostReportsItemDataModel,
    tableIndex: number
  ): TableRow {
    let theoretical_total = 0;
    let total = 0;
    let total_comparative_deviation = 0;
    let water_cost = 0;
    let numValves = 0;

    const costs = this.createEmptyCosts(
      this.costsReportsData.time_ranges.length
    );

    currencyData.fields.forEach((fieldData) => {
      fieldData.terminals.forEach((terminalData) => {
        terminalData.water_systems.forEach((waterSystem) => {
          waterSystem.groups.forEach((groupData) => {
            groupData.valves.forEach((valveData) => {
              if (
                this.isExcludedValve(
                  tableIndex,
                  fieldData.field_name,
                  terminalData.terminal_name,
                  'C' + (waterSystem.number + 1),
                  groupData.name,
                  valveData.name
                )
              ) {
                return;
              }
              numValves++;
              this.sumValveCosts(costs, valveData.costs);
              theoretical_total += valveData.totals.theoretical_total;
              total += valveData.totals.total;
              total_comparative_deviation +=
                valveData.totals.total_comparative_deviation;
              water_cost += valveData.totals.water_cost;
            });
          });
        });
      });
    });
    costs.forEach((cost) => {
      cost.water_price_m3 = cost.water_price_m3 / numValves;
    });

    return {
      group: this.translationsLib.get('irrigation_total'),
      group_show_cell: true,
      valve_show_cell: false,
      group_colspan: 5,
      group_rowspan: 1,
      is_total_total: true,
      is_totals: true,
      is_last_group_valve: false,
      is_last_water_system_group: false,
      costs,
      theoretical_total,
      total,
      total_comparative_deviation,
      water_cost,
      currency: {
        symbol: currencyData.currency.symbol,
        space_separator: currencyData.currency.space_separator,
        symbol_position: currencyData.currency.symbol_position,
      },
    };
  }

  calculateGroupRowSpan(
    groupData: CostsReportsGroupDataModel,
    waterSystemData: CostsReportsWaterSystemDataModel,
    fieldData: CostsReportsFieldDataModel,
    terminalData: CostsReportsTerminalDataModel,
    tableIndex: number
  ): number {
    return groupData.valves.filter((valveData) => {
      return !this.isExcludedValve(
        tableIndex,
        fieldData.field_name,
        terminalData.terminal_name,
        'C' + (waterSystemData.number + 1),
        groupData.name,
        valveData.name
      );
    }).length;
  }

  calculateWaterSystemRowSpan(
    waterSystemData: CostsReportsWaterSystemDataModel,
    fieldData: CostsReportsFieldDataModel,
    terminalData: CostsReportsTerminalDataModel,
    tableIndex: number
  ): number {
    let rowspan = 0;
    waterSystemData.groups.forEach((groupData) => {
      rowspan += this.calculateGroupRowSpan(
        groupData,
        waterSystemData,
        fieldData,
        terminalData,
        tableIndex
      );
    });
    if (rowspan > 0) {
      rowspan++;
    }
    return rowspan;
  }

  calculateTerminalRowSpan(
    terminalData: CostsReportsTerminalDataModel,
    fieldData: CostsReportsFieldDataModel,
    tableIndex: number
  ): number {
    let rowspan = 0;
    terminalData.water_systems.forEach((waterSystemData) => {
      rowspan += this.calculateWaterSystemRowSpan(
        waterSystemData,
        fieldData,
        terminalData,
        tableIndex
      );
    });
    if (rowspan > 0) {
      rowspan++;
    }
    return rowspan;
  }

  calculateFieldRowSpan(
    fieldData: CostsReportsFieldDataModel,
    tableIndex: number
  ): number {
    let rowspan = 0;
    fieldData.terminals.forEach((terminalData) => {
      rowspan += this.calculateTerminalRowSpan(
        terminalData,
        fieldData,
        tableIndex
      );
    });
    if (rowspan > 0) {
      rowspan++;
    }
    return rowspan;
  }

  additionalClass(element: TableRow): string {
    const classList = [];

    if (element.is_total_water_system) {
      classList.push('total-water-system-line');
    }
    if (element.is_total_terminal) {
      classList.push('total-terminal-line');
    }
    if (element.is_total_field) {
      classList.push('total-field-line');
    }
    if (element.is_total_total) {
      classList.push('total-total-line');
    }

    return classList.join(' ');
  }

  round(value: number, precision: number = 2): number {
    return Number(value.toFixed(precision));
  }

  amount(
    amount: number,
    currency: {
      symbol: string;
      symbol_position: string;
      space_separator: boolean;
    },
    precision: number = 2
  ): string {
    const space = currency.space_separator ? '\u00A0' : '';
    const roundedAmount = this.round(amount, precision);
    if (currency.symbol_position === 'left') {
      return `${currency.symbol}${space}${roundedAmount}`;
    } else {
      return `${roundedAmount}${space}${currency.symbol}`;
    }
  }

  toggleShowDetails(value: boolean): void {
    this.showDetails = value;
  }

  valveIdentifier(
    field: string,
    terminal: string,
    water_system: string,
    group: string,
    valve: string,
    separator: string = ' - '
  ): string {
    return (
      field +
      separator +
      terminal +
      separator +
      water_system +
      separator +
      group +
      separator +
      valve
    );
  }

  isExcludedValve(
    tableIndex: number,
    field_name: string,
    terminal_name: string,
    water_system_name: string,
    group_name: string,
    valve_name: string
  ): boolean {
    if (this.valvesForm.length === 0) {
      return false;
    }

    return !this.valvesForm[tableIndex].value.includes(
      this.valveIdentifier(
        field_name,
        terminal_name,
        water_system_name,
        group_name,
        valve_name
      )
    );
  }

  hasLines(tableData: TableRow[]): boolean {
    let hasLines = false;
    tableData.forEach((row) => {
      if (!row.is_totals) {
        hasLines = true;
      }
    });

    return hasLines;
  }
}

interface TableRow {
  field?: string;
  terminal?: string;
  terminal_vid?: string;
  water_system?: string;
  group?: string;
  valve?: string;

  costs: CostsReportsCostDataModel[];

  theoretical_total: number;
  total: number;
  total_comparative_deviation: number;
  water_cost: number;

  field_rowspan?: number;
  terminal_rowspan?: number;
  water_system_rowspan?: number;
  group_rowspan?: number;

  field_colspan?: number;
  terminal_colspan?: number;
  water_system_colspan?: number;
  group_colspan?: number;

  field_show_cell?: boolean;
  terminal_show_cell?: boolean;
  water_system_show_cell?: boolean;
  group_show_cell?: boolean;
  valve_show_cell?: boolean;
  is_last_group_valve: boolean;
  is_last_water_system_group: boolean;

  currency: {
    symbol: string;
    space_separator: boolean;
    symbol_position: string;
  };

  is_totals: boolean;

  is_total_water_system?: boolean;
  is_total_terminal?: boolean;
  is_total_field?: boolean;
  is_total_total?: boolean;
}

interface ValveSelectOption {
  value: string;
  label: string;
}
